/**
 * useAvlQuery — Supplier S1 (Approved Vendor List).
 *
 * GET /api/v1/avl → the material ↔ supplier approval matrix: which approved
 * suppliers may supply which material, with per-cell status + single-source and
 * expiring-qualification flags. Columns are the real suppliers.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type AvlStatus = 'APPROVED' | 'CONDITIONAL' | 'DISQUALIFIED';

export interface AvlMaterial {
  material_id: string;
  name: string;
  category: string;
  criticality: string;
  approved_supplier_count: number;
  single_source: boolean;
}
export interface AvlSupplier {
  supplier_id: string;
  name: string;
  subtype: string;
  criticality: string;
  lifecycle_status: string;
  risk_category: string | null;
}
export interface AvlCell {
  material_id: string;
  supplier_id: string;
  status: AvlStatus;
  qualified_until: string | null;
  is_primary: boolean;
}
export interface AvlResponse {
  materials: AvlMaterial[];
  suppliers: AvlSupplier[];
  matrix: AvlCell[];
  summary: {
    materials: number;
    suppliers: number;
    approved_pairs: number;
    conditional_pairs: number;
    single_source_materials: number;
    expiring_90d: number;
  };
}

export function useAvlQuery(): UseQueryResult<AvlResponse> {
  return useQuery<AvlResponse>({
    queryKey: ['suppliers', 'avl'],
    queryFn: ({ signal }) => apiFetch<AvlResponse>('/api/v1/avl', { signal }),
    staleTime: 60_000,
  });
}
